/**
 * Theme preference: follow the OS (`system`) or pin light/dark.
 * The choice is stamped on `<html data-theme>` so the token file's manual
 * override scope wins over `prefers-color-scheme`, and remembered locally.
 */
import { useSyncExternalStore } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'vam.theme';
const listeners = new Set<() => void>();

let preference: ThemePreference = readStored();

function readStored(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* private mode, blocked storage: fall back to the OS setting */
  }
  return 'system';
}

function stamp(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
}

/** Apply the remembered preference; call once before the first paint. */
export function initTheme(): void {
  stamp();
}

export function setThemePreference(next: ThemePreference): void {
  preference = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore: the preference simply does not survive the session */
  }
  stamp();
  for (const listener of listeners) listener();
}

export function getThemePreference(): ThemePreference {
  return preference;
}

/** The mode actually rendered right now, resolving `system`. */
export function resolvedTheme(): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, getThemePreference, () => 'system' as const);
}
